export const capitalize = (str: string): string => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatDateWithDifference = (inputDate: string | Date): string => {
	if (!inputDate) {
		return '';
	}

	const date =
		typeof inputDate === 'string' ? new Date(inputDate) : inputDate;
	const now = new Date();

	const diffMs = Math.abs(now.getTime() - date.getTime());

	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	const diffYears = Math.floor(diffDays / 365);

	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();
	const formattedDate = `${day}/${month}/${year}`;

	let differenceString = '';
	switch (true) {
		case diffHours < 24:
			differenceString =
				diffHours === 1 ? '1 hora' : `${diffHours} horas`;
			break;
		case diffDays < 365:
			differenceString = diffDays === 1 ? '1 dia' : `${diffDays} dias`;
			break;
		default:
			differenceString = diffYears === 1 ? '1 ano' : `${diffYears} anos`;
			break;
	}

	return `${formattedDate} (${differenceString})`;
};

/** Data local dd/MM/yyyy + hora HH:mm (sem “há X dias”). */
export const formatDateWithTime = (inputDate: string | Date): string => {
	if (!inputDate) return '';

	const date =
		typeof inputDate === 'string' ? new Date(inputDate) : inputDate;
	if (isNaN(date.getTime())) return '';

	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');

	return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export function getWeekday(dateString: string): string {
	const date = new Date(dateString);

	if (isNaN(date.getTime())) {
		throw new Error('Invalid date string');
	}

	const weekdayIndex = date.getDay();

	const weekdays = [
		'domingo',
		'segunda',
		'terça',
		'quarta',
		'quinta',
		'sexta',
		'sábado',
	];

	return weekdays[weekdayIndex];
}

export const formatTimeSlot = (start?: string, end?: string) => {
	if (!start || !end) return null;

	const formatTimePart = (date: Date) => {
		const hours = date.getHours().toString().padStart(2, '0');
		const minutes = date.getMinutes().toString().padStart(2, '0');
		return `${hours}:${minutes}`;
	};

	const startDate = new Date(start);
	const endDate = new Date(end);

	return `${formatTimePart(startDate)} - ${formatTimePart(endDate)}`;
};

export function formatISODateToDDMMYYYY(
	isoDateString: string | undefined,
): string {
	if (!isoDateString) return '';

	const date = new Date(isoDateString);

	if (isNaN(date.getTime())) {
		throw new Error('Invalid date string');
	}

	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const year = date.getFullYear();

	return `${day}/${month}/${year}`;
}

export const formatDateForInput = (isoDate?: string | null): string => {
	if (!isoDate) return '';

	const date = new Date(isoDate);
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

export const parsePtBrDate = (dateString: string) => {
	if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) return null;

	const parts = dateString.split('/');
	const day = parseInt(parts[0], 10);
	const month = parseInt(parts[1], 10);
	const year = parseInt(parts[2], 10);

	if (day < 1 || day > 31 || month < 1 || month > 12) {
		throw new Error('Invalid date values');
	}

	const date = new Date(year, month - 1, day);

	if (
		date.getDate() !== day ||
		date.getMonth() !== month - 1 ||
		date.getFullYear() !== year
	) {
		throw new Error('Invalid date: ' + dateString);
	}

	return date;
};

export const transformBirthday = (dateStr: string) => {
	const cleaned = dateStr.replace(/\D/g, '');
	const day = cleaned.slice(0, 2);
	const month = cleaned.slice(2, 4);
	const year = cleaned.slice(4, 8);

	if (day.length !== 2 || month.length !== 2 || year.length !== 4) {
		throw new Error('Invalid date format. Use dd/mm/yyyy');
	}

	const isoDate = new Date(
		`${year}-${month}-${day}T00:00:00.000Z`,
	).toISOString();

	return isoDate;
};

/**
 * Normaliza campos de nome:
 * - remove espaços do início/fim
 * - colapsa múltiplos espaços internos
 *
 * Ex: "   Rodrigo     da Mota   " -> "Rodrigo da Mota"
 */
export const normalizeName = (value: string): string => {
	if (!value) return '';
	return value.replace(/\s+/g, ' ').trim();
};

/**
 * Formats a CRP string into the standard mask (XX/XXXXX).
 * @param crp - The raw CRP input (e.g., "0612345" → "06/12345")
 * @returns The formatted CRP string or an empty string if invalid.
 */
export const maskCRP = (crp: string): string => {
	if (!crp) return '';

	// Remove all non-digit characters
	const digits = crp.replace(/\D/g, '');

	// Apply the mask: XX/XXXXX
	if (digits.length <= 2) {
		return digits;
	} else {
		return `${digits.slice(0, 2)}/${digits.slice(2, 7)}`;
	}
};
